import { useCallback, useEffect, useState } from "react";
import { TransformRuleFactory } from "../../models/transformation/factory/TransformRuleFactory";
import {
  ReplaceDate,
  ReplaceDateData,
} from "../../models/transformation/ReplaceDate";
import "../../styles/transformation/replaceDateEditor.css";
import { getTransformRuleTypeDisplayValue } from "../../types/transformation/TransformationRuleTypes";
import { Button } from "../buttons/Button";
import Icon from "../Icon";
import { InputGroup } from "../inputGroup/InputGroup";
import { Label } from "../inputGroup/Label";
import { TransformTypeLabel } from "../inputGroup/TransformTypeLabel";
import { DropdownInput } from "../inputs/DropdownInput";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import { TTTransformDeleteRule } from "../tooltips/ConfigurationTooltips";
import {
  TTReplaceDateLabelInput,
  TTReplaceDateLabelTarget,
} from "../tooltips/TTTransformation";
import { TransformRuleMatcherEditorChildProps } from "./TransformRuleEditor";

export { ReplaceDateEditor };

type Props = TransformRuleMatcherEditorChildProps<ReplaceDate>;

const commonDateFormats = [
  "dd/MM/yyyy", // standard date format
  "MM/dd/yyyy", // US date format
  "yyyy-MM-dd", // programmer format
  "yyyyMMdd", // crazy format
];

const ReplaceDateEditor = (props: Props) => {
  const rule = TransformRuleFactory.createRule(
    props.rule.toData(),
  ) as ReplaceDate;

  if (props.display === "view") {
    return <Display rule={rule} />;
  }
  return (
    <Editor
      fields={props.fields}
      rule={rule}
      update={props.update}
      variables={props.variables}
      button={props.button}
      changedRuleType={props.changedRuleType}
      index={props.index}
      onClickLabel={props.onClickLabel}
      label={props.label}
    />
  );
};
type DisplayProps = {
  rule: ReplaceDateData;
};

const Display = (props: DisplayProps) => {
  // add Key
  const rule = props.rule;
  return (
    <div className={"transformRuleEditor replaceDateEditor"}>
      <span>{rule.position}</span>

      {/* edit Input Date Format value */}
      <InputGroup>
        <Label>{getTransformRuleTypeDisplayValue(rule.discriminator)}</Label>

        <input type={"text"} value={rule.inputDateFormat} disabled />
      </InputGroup>
      {/* edit Target Format value */}
      <InputGroup>
        <Label>Target Format</Label>

        <input type={"text"} value={rule.inputDateFormat} disabled />
      </InputGroup>
    </div>
  );
};

type EditorProps = Omit<Props, "display" | "rule"> & { rule: ReplaceDate };
const Editor = (props: EditorProps) => {
  const rule = props.rule;
  const [targetFormat, setTargetFormat] = useState(rule.targetFormat);
  const [inputFormat, setInputFormat] = useState(rule.inputDateFormat);

  const trigger = useCallback(() => {
    rule.targetFormat = targetFormat;
    rule.inputDateFormat = inputFormat;
    props.update(rule);
  }, [targetFormat, inputFormat]);

  useEffect(() => {
    if (
      !(
        targetFormat === rule.targetFormat &&
        inputFormat === rule.inputDateFormat
      )
    ) {
      trigger();
    }
  }, [targetFormat, inputFormat]);

  return (
    <div className={"transformRuleEditor replaceDateEditor"}>
      <span>{rule.position}</span>

      {/* edit Input Date Format value */}
      <InputGroup button className="inputFormatIg">
        <AdvancedTooltip short tooltipText={TTReplaceDateLabelInput}>
          {(tProps) => (
            <>
              {props.changedRuleType && (
                <TransformTypeLabel
                  {...tProps}
                  discriminator={rule.getDiscriminator()}
                  onTypeChange={(type) =>
                    props.changedRuleType!(
                      TransformRuleFactory.changeRule(rule.toData(), type),
                    )
                  }
                >
                  {getTransformRuleTypeDisplayValue(rule.getDiscriminator())}
                </TransformTypeLabel>
              )}
              {!props.changedRuleType && (
                <Label {...tProps}>
                  {getTransformRuleTypeDisplayValue(rule.getDiscriminator())}
                </Label>
              )}
            </>
          )}
        </AdvancedTooltip>

        <DropdownInput
          className="midInputGroup"
          value={inputFormat}
          onChange={(x) => setInputFormat(x)}
          suggestions={commonDateFormats}
          filterFn={(val, sugg) =>
            sugg.toLowerCase().startsWith(val.toLowerCase())
          }
        />

        <AdvancedTooltip overflow short tooltipText={TTTransformDeleteRule}>
          {(tProps) => (
            <Button
              {...tProps}
              onClick={() =>
                // delete
                props.update(null)
              }
            >
              <Icon altText={""} icon={"trash"} />
            </Button>
          )}
        </AdvancedTooltip>
      </InputGroup>

      {/* edit Target Format value */}
      <InputGroup className="targetFormatIg">
        <AdvancedTooltip short tooltipText={TTReplaceDateLabelTarget}>
          {(tProps) => <Label {...tProps}>Target Format</Label>}
        </AdvancedTooltip>
        <DropdownInput
          className="midInputGroup"
          value={targetFormat}
          onChange={(x) => setTargetFormat(x)}
          suggestions={commonDateFormats}
          filterFn={(val, sugg) =>
            sugg.toLowerCase().startsWith(val.toLowerCase())
          }
        />
      </InputGroup>
    </div>
  );
};
